import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, TrendingUp, TrendingDown, Minus, Globe, Newspaper, Hash, Palette, Zap, Cloud } from "lucide-react";

// Detect emotion type from adjective
const getEmotionType = (adjective) => {
  if (!adjective) return "neutral";
  const adj = adjective.toLowerCase();
  
  if (/angry|rage|fury|furious|hostile|outraged|indignant|resentful|bitter/.test(adj)) {
    return "anger";
  } else if (/anxious|worried|nervous|uneasy|apprehensive|tense|stressed|agitated|frantic|panicked/.test(adj)) {
    return "anxiety";
  } else if (/sad|melancholy|gloomy|somber|depress|grief|mourn|sorrowful|despair|despondent|forlorn|wretched/.test(adj)) {
    return "sadness";
  } else if (/peaceful|calm|serene|tranquil|relaxed|content|balanced|harmonious|still|quiet/.test(adj)) {
    return "calm";
  } else if (/hopeful|optimistic|encourage|uplift|inspir|enthus|excit|elat|radiant|lumin|efferves|happy|joyful|jubilant|euphoric|buoyant|sanguine/.test(adj)) {
    return "hope";
  } else if (/fear|terror|dread|horrif|alarm|frighten|scary|ominous|terrified|aghast/.test(adj)) {
    return "fear";
  } else if (/confus|uncertain|perplex|bewilder|puzzl|unsure|ambivalent|conflicted/.test(adj)) {
    return "confusion";
  } else if (/turbulent|volatile|chaotic|unstable|erratic|tumultuous|fractured|shattered/.test(adj)) {
    return "turbulence";
  }
  return "neutral";
};

// Get emotion label for display
const getEmotionLabel = (adjective) => {
  const type = getEmotionType(adjective);
  const labels = {
    anger: "anger",
    anxiety: "anxiety",
    sadness: "sadness",
    calm: "calm",
    hope: "hope",
    fear: "fear",
    confusion: "confusion",
    turbulence: "turbulence",
    neutral: "neutral"
  };
  return labels[type] || "neutral";
};

const MoodDisplay = ({ mood, color, onMoodClick }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  
  if (!mood) {
    return (
      <div className="mood-display flex items-center justify-center h-full">
        <div className="text-center">
          <motion.div
            className="w-20 h-20 rounded-full mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <p className="apple-headline">{t('analyzing')}</p>
        </div>
      </div>
    );
  }

  const emotionType = getEmotionType(mood.adjective);
  const emotionLabel = getEmotionLabel(mood.adjective);
  
  // Extract contributing factors from mood data
  const regionalKeywords = mood.regional_keywords || {};
  const events = mood.events || [];
  const keywords = mood.keywords || [];
  const intensity = mood.intensity || 0.5;
  const sourcesCount = mood.sources_used?.length || 0;
  const headlinesCount = mood.headlines_analyzed || 0;

  const handleWordClick = () => {
    setShowModal(true);
    if (onMoodClick) onMoodClick();
  };

  return (
    <div className={`mood-display mood-emotion-${emotionType}`}>
      {/* Animated mood orb */}
      <div className="mood-orb-container" data-testid="mood-orb">
        <motion.div
          className="mood-orb"
          style={{ 
            background: `radial-gradient(circle at 30% 30%, ${color}88, ${color}dd, ${color})`,
            boxShadow: `0 0 60px ${color}66, 0 0 100px ${color}33`
          }}
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: emotionType === 'calm' ? 6 : emotionType === 'anxiety' ? 1.5 : 3,
            repeat: Infinity,
            ease: emotionType === 'calm' ? 'easeInOut' : emotionType === 'anxiety' ? 'easeOut' : 'easeInOut'
          }}
        >
          <div className="mood-orb-ring" style={{ borderColor: `${color}44` }} />
          <div className="mood-orb-ring mood-orb-ring-2" style={{ borderColor: `${color}22` }} />
        </motion.div>
      </div>

      {/* Emotion label */}
      <div className="mood-emotion-label" style={{ color: `${color}99` }}>
        {emotionLabel.toUpperCase()}
      </div>

      {/* Main mood adjective - CLICKABLE */}
      <motion.h1 
        className="mood-adjective mood-adjective-clickable"
        style={{ color, cursor: 'pointer' }}
        data-testid="mood-adjective"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        key={mood.adjective}
        onClick={handleWordClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        title="Click to see mood analysis"
      >
        {mood.adjective}
      </motion.h1>

      {/* Pantone reference */}
      {mood.pantone && (
        <div className="pantone-badge">
          <div className="pantone-swatch" style={{ backgroundColor: color }} />
          <span className="pantone-name">{mood.pantone.name}</span>
        </div>
      )}

      {/* Mood Analysis Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="mood-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            data-testid="mood-analysis-modal"
          >
            <motion.div
              className="mood-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="mood-modal-header">
                <div className="mood-modal-title-row">
                  <div 
                    className="mood-modal-color-dot"
                    style={{ backgroundColor: color }}
                  />
                  <h2 className="mood-modal-title" style={{ color }}>
                    {mood.adjective.toUpperCase()}
                  </h2>
                </div>
                <button 
                  className="mood-modal-close"
                  onClick={() => setShowModal(false)}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Color & Intensity Section */}
              <div className="mood-modal-section">
                <div className="mood-modal-section-header">
                  <Palette size={14} />
                  <span>Color & Intensity</span>
                </div>
                <div className="mood-modal-color-info">
                  <div 
                    className="mood-modal-swatch"
                    style={{ backgroundColor: color }}
                  />
                  <div className="mood-modal-color-details">
                    <span className="mood-modal-pantone">{mood.pantone?.name || 'Custom Color'}</span>
                    <span className="mood-modal-hex">{color}</span>
                  </div>
                  <div className="mood-modal-intensity">
                    <span className="intensity-label">Intensity</span>
                    <div className="intensity-bar">
                      <div 
                        className="intensity-fill"
                        style={{ width: `${intensity * 100}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="intensity-value">{Math.round(intensity * 100)}%</span>
                  </div>
                </div>
              </div>

              {/* Contributing Factors */}
              <div className="mood-modal-section">
                <div className="mood-modal-section-header">
                  <Zap size={14} />
                  <span>Contributing Factors</span>
                </div>
                <div className="mood-modal-factors">
                  <div className="factor-item">
                    <Newspaper size={14} />
                    <span>{headlinesCount} headlines analyzed</span>
                  </div>
                  <div className="factor-item">
                    <Globe size={14} />
                    <span>{sourcesCount} global sources</span>
                  </div>
                </div>
              </div>

              {/* Key Topics */}
              {keywords.length > 0 && (
                <div className="mood-modal-section">
                  <div className="mood-modal-section-header">
                    <Hash size={14} />
                    <span>Key Topics Driving This Mood</span>
                  </div>
                  <div className="mood-modal-keywords">
                    {keywords.slice(0, 8).map((kw, idx) => (
                      <span 
                        key={idx} 
                        className="mood-keyword-tag"
                        style={{ 
                          borderColor: `${color}40`,
                          color: color
                        }}
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Regional Synopsis */}
              {Object.keys(regionalKeywords).length > 0 && (
                <div className="mood-modal-section">
                  <div className="mood-modal-section-header">
                    <Globe size={14} />
                    <span>Regional Trends</span>
                  </div>
                  <div className="mood-modal-regions">
                    {Object.entries(regionalKeywords).slice(0, 4).map(([region, trends]) => (
                      <div key={region} className="region-trend">
                        <span className="region-name">{region}</span>
                        <span className="region-keywords">
                          {Array.isArray(trends) ? trends.slice(0, 2).join(', ') : trends}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Major Events */}
              {events.length > 0 && (
                <div className="mood-modal-section">
                  <div className="mood-modal-section-header">
                    {events[0]?.sentiment === 'positive' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    <span>Major Events</span>
                  </div>
                  <div className="mood-modal-events">
                    {events.slice(0, 3).map((event, idx) => (
                      <div 
                        key={idx} 
                        className={`event-item ${event.sentiment || 'neutral'}`}
                      >
                        <span className="event-impact">
                          {event.sentiment === 'positive' ? <TrendingUp size={12} /> : 
                           event.sentiment === 'negative' ? <TrendingDown size={12} /> : 
                           <Minus size={12} />}
                        </span>
                        <span className="event-text">{event.event || event.headline || event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Synopsis */}
              <div className="mood-modal-synopsis">
                <p>
                  The global mood is currently <strong style={{ color }}>{mood.adjective}</strong> with an 
                  intensity of {Math.round(intensity * 100)}%. This assessment is based on analysis of {headlinesCount} headlines 
                  from {sourcesCount} news sources worldwide. 
                  {keywords.length > 0 && ` Key themes include ${keywords.slice(0, 3).join(', ')}.`}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { MoodDisplay, getEmotionType, getEmotionLabel };
